import { core, formatDay, goals as goalsNs, investments as investmentsNs, type Day, type GoalProgress } from '@map-my-money/engine';
import { definitionsFor } from './definitions.ts';
import { linePoints, monthKey, type LinePoint } from './line.ts';
import { readableMonth } from './stamp.ts';
import { money, round1 } from './money.ts';
import { runForecast, type ViewInput } from './run.ts';
import { mortgageFreeDate } from './mortgage.ts';

export interface GoalView {
  readonly id: string;
  readonly label: string;
  readonly series: string;
  readonly target: string;
  readonly current: string;
  readonly progress_percent: number;
  readonly achieved: boolean;
  readonly eta: string | null;
  readonly months_away: number | null;
  /** Only for a goal with a "by" day (R26, decision 6): that day, and how the goal's date compares with it. */
  readonly by?: string;
  readonly on_track?: boolean;
  /** Whole calendar months the goal's month is after the by month; 0 on track; null when not within the horizon. */
  readonly months_behind?: number | null;
  /** Whole calendar months the goal's month (this month once met) is before the by month. */
  readonly months_ahead?: number;
  /** How far from the target the goal's series is at the end of the by month (now, once that month has passed); 0 on track. */
  readonly shortfall?: string;
}

export const monthsAway = (date: Day | null, today: Day): number | null => (date === null ? null : Math.max(0, core.monthsBetween(today, date)));

export function goalView(g: GoalProgress, today: Day): GoalView {
  const months = monthsAway(g.estimatedDate, today);
  return {
    id: g.goal.id,
    label: g.goal.label,
    series: g.goal.series,
    target: money(g.targetValue),
    current: money(g.currentValue),
    progress_percent: round1(g.progressPercent),
    achieved: g.isAchieved,
    eta: g.estimatedDate === null ? null : formatDay(g.estimatedDate),
    months_away: g.isAchieved ? 0 : months,
    ...(g.deadline
      ? {
          by: formatDay(g.deadline.date),
          on_track: g.deadline.onTrack,
          months_behind: g.deadline.monthsBehind,
          months_ahead: g.deadline.monthsAhead,
          shortfall: money(g.deadline.shortfall),
        }
      : {}),
  };
}

/** How far every surface looks for a goal's date (R26). */
export const GOALS_HORIZON_YEARS = goalsNs.GOAL_HORIZON_YEARS;

export function goalsView(input: ViewInput, years = GOALS_HORIZON_YEARS) {
  const f = runForecast(input, years);
  const star = goalsNs.northStar(f.goals);
  const debtFree = f.forecast.metrics.debtFreeDate;
  const mortgageFree = mortgageFreeDate(f.plan, f.today);
  return {
    view: 'goals' as const,
    as_of: input.asOf,
    years,
    north_star_id: star?.goal.id ?? null,
    goals: [...f.goals].sort((a, b) => a.goal.priority - b.goal.priority).map((g) => goalView(g, f.today)),
    debt_free_date: debtFree === null ? null : formatDay(debtFree),
    mortgage_free_date: mortgageFree === null ? null : formatDay(mortgageFree),
    definitions: definitionsFor('goal_progress', 'goals_horizon', 'debt_free', 'mortgage_free'),
  };
}

export type GoalsResult = ReturnType<typeof goalsView>;

export type GoalSeries = GoalProgress['goal']['series'];

/** A goal drawn against the series it tracks: the line, its target, its "by" day and where the line reaches it. */
export interface GoalLine {
  readonly series: GoalSeries;
  readonly points: readonly { readonly date: Day; readonly value: number }[];
  readonly target: number;
  readonly by: Day | null;
  /** The point on the line in the goal's month (now once met); null when it isn't reached within the line. */
  readonly reached: { readonly date: Day; readonly value: number } | null;
}

/**
 * One goal's own line over `years` (R20 points: now, then each month's end): cash for a deposit or a
 * rainy-day fund, debt for debt-free, net worth or investments for theirs, and for a passive-income
 * goal the monthly income cash and investments could pay at the plan's safe withdrawal rate.
 */
export function goalLine(input: ViewInput, goalId: string, years: number): GoalLine | null {
  const f = runForecast(input, years);
  const g = f.goals.find((p) => p.goal.id === goalId);
  if (!g) return null;
  const series = g.goal.series;
  const rate = f.plan.configuration.safeWithdrawalRatePercent;
  const valueOf = (p: LinePoint) => {
    switch (series) {
      case 'cash': return p.cash;
      case 'investment': return p.investments;
      case 'debt': return p.debt;
      case 'netWorth': return p.netWorth;
      case 'passive': return investmentsNs.safeWithdrawalMonthlyIncome(core.add(p.cash, p.investments), rate);
    }
  };
  const line = linePoints(f);
  const points = line.map((p) => ({ date: p.date, value: Number(money(valueOf(p))) }));
  const eta = g.isAchieved ? f.today : g.estimatedDate;
  const reachedAt = eta === null ? -1 : g.isAchieved ? 0 : points.findIndex((p, i) => i > 0 && (monthKey(p.date) === monthKey(eta) || p.date > eta));
  return {
    series,
    points,
    target: Number(money(g.targetValue)),
    by: g.deadline?.date ?? null,
    reached: reachedAt < 0 ? null : points[reachedAt]!,
  };
}

const unit = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** A month count in years and months, as every goal line words it: "2 months", "2 years", "5 years 2 months". */
export function durationText(months: number): string {
  const m = Math.max(0, Math.round(months));
  const years = Math.floor(m / 12);
  const rest = m % 12;
  if (years === 0) return unit(rest, 'month');
  return rest === 0 ? unit(years, 'year') : `${unit(years, 'year')} ${unit(rest, 'month')}`;
}

type GoalStatusFields = Pick<GoalView, 'achieved' | 'eta' | 'by' | 'on_track' | 'months_behind' | 'months_ahead' | 'shortfall'>;

/**
 * A goal's date in words, the same on every surface (R26): "Reached this month", "On course for
 * Dec 2026", "Not within 50 years"; with a "by" day "On track for Mar 2027 · 3 months before your
 * date" or "5 years 2 months after your Jan 2028 date · Mar 2033", never "behind" or "late".
 * The shortfall is its own line: `goalShortfallText`.
 */
export function goalStatusText(g: GoalStatusFields): string {
  if (g.achieved) return 'Reached this month';
  const when = g.eta === null ? `Not within ${GOALS_HORIZON_YEARS} years` : readableMonth(g.eta);
  if (g.by === undefined) return g.eta === null ? when : `On course for ${when}`;
  if (g.on_track) return `On track for ${when}${g.months_ahead ? ` · ${durationText(g.months_ahead)} before your date` : ''}`;
  return g.months_behind ? `${durationText(g.months_behind)} after your ${readableMonth(g.by)} date · ${when}` : when;
}

/** "£3,000 short by Jan 2027" for a goal whose date is after its "by" day; null otherwise. `money` formats the amount. */
export function goalShortfallText(g: GoalStatusFields, money: (amount: string) => string): string | null {
  if (g.achieved || g.by === undefined || g.on_track || g.shortfall === undefined || !(Number(g.shortfall) > 0)) return null;
  return `${money(g.shortfall)} short by ${readableMonth(g.by)}`;
}
